import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../../theme';
import { PhoneInputComponent } from '../../design-system/PhoneInput';
import { Input } from '../../design-system/Input';
import { usePhoneValidation } from '../hooks/usePhoneValidation';
import { parseInternationalPhoneNumber } from '../../../utils/phoneNumber';
import { RecipientChip } from '../types/messageForm.types';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../../../utils/businessDefaults';

interface RecipientChipsInputProps {
  recipients: RecipientChip[];
  onAdd: (chip: RecipientChip) => void;
  onRemove: (id: string) => void;
  onImportFromDevice?: () => void;
  onImportFromFile?: () => void;
  onDownloadTemplate?: () => void;
  isDisabled?: boolean;
}

export const RecipientChipsInput: React.FC<RecipientChipsInputProps> = ({
  recipients,
  onAdd,
  onRemove,
  onImportFromDevice,
  onImportFromFile,
  onDownloadTemplate,
  isDisabled,
}) => {
  const styles = useThemedStyles(getStyles);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState(() => defaultPhoneCountry());
  const phoneRef = useRef<any>(null);
  const { extractPhoneData } = usePhoneValidation();

  // When user types a full international number (+...) auto-detect the country
  // so the flag/dial-code selector stays in sync with what they typed.
  useEffect(() => {
    const trimmed = phone.trim();
    if (trimmed.startsWith('+') && trimmed.length > 4) {
      const parsed = parseInternationalPhoneNumber(trimmed);
      if (parsed?.country && parsed.country !== country) {
        setCountry(parsed.country);
        if (phoneRef.current?.setCountry) {
          phoneRef.current.setCountry(parsed.country);
        }
      }
    }
  }, [phone]);

  const handleAdd = useCallback(() => {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) return;

    let formattedPhone: string;
    let resolvedCountryCode: string;
    let resolvedDialCode: string;

    if (trimmedPhone.startsWith('+')) {
      // Full international number typed — parse it directly to avoid the
      // PhoneInput prepending the dial code a second time.
      const parsed = parseInternationalPhoneNumber(trimmedPhone);
      if (parsed) {
        formattedPhone = parsed.e164Format || trimmedPhone;
        resolvedCountryCode = parsed.country || country;
        resolvedDialCode = parsed.countryCode || '+20';
      } else {
        formattedPhone = trimmedPhone;
        resolvedCountryCode = country;
        resolvedDialCode = '+20';
      }
    } else {
      // National number — combine with dial code from the PhoneInput selector.
      formattedPhone = trimmedPhone;
      resolvedCountryCode = country;
      resolvedDialCode = '+20';

      if (phoneRef.current) {
        const phoneData = phoneRef.current.getValue();
        const extracted = extractPhoneData(phoneData, trimmedPhone);
        formattedPhone = extracted.normalized;
        resolvedCountryCode = extracted.countryCode || country;
        resolvedDialCode = extracted.dialCode || '+20';
      }

      // extractPhoneData returns '' for a still-masked value rather than
      // manufacturing a garbage number from its visible digits.
      if (!formattedPhone) {
        return;
      }

      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }

      // Final E.164 normalisation — guards against double dial-code like +20201234567890
      const reParsed = parseInternationalPhoneNumber(formattedPhone);
      if (reParsed) {
        formattedPhone = reParsed.e164Format || formattedPhone;
        resolvedCountryCode = reParsed.country || resolvedCountryCode;
        resolvedDialCode = reParsed.countryCode || resolvedDialCode;
      }
    }

    // Deduplicate
    if (recipients.some(r => r.formattedPhone === formattedPhone)) {
      return;
    }

    const chip: RecipientChip = {
      id: `chip-${Date.now()}`,
      displayName: name.trim() || formattedPhone,
      formattedPhone,
      dialCode: resolvedDialCode,
      countryCode: resolvedCountryCode,
      isExisting: false,
      isValid: true,
    };

    onAdd(chip);
    setPhone('');
    setName('');
  }, [phone, name, country, recipients, onAdd, extractPhoneData]);

  const MAX_VISIBLE_CHIPS = 10;

  const visibleRecipients = recipients.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = recipients.length - MAX_VISIBLE_CHIPS;

  return (
    <View style={styles.container}>
      {/* Chips */}
      {recipients.length > 0 && (
        <View style={styles.chipsWrap}>
          {visibleRecipients.map(chip => (
            <View
              key={chip.id}
              style={[
                styles.chip,
                chip.isExisting && styles.chipExisting,
                !chip.isValid && styles.chipInvalid,
              ]}
            >
              <Ionicons
                name={chip.isExisting ? 'person' : 'call-outline'}
                size={13}
                color={chip.isExisting ? colors.success : colors.text.secondary}
                style={styles.chipIcon}
              />
              <Text style={styles.chipText} numberOfLines={1}>
                {chip.displayName}
              </Text>
              <TouchableOpacity
                onPress={() => onRemove(chip.id)}
                disabled={isDisabled}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={styles.chipClose}
              >
                <Ionicons name="close" size={14} color={colors.text.secondary} />
              </TouchableOpacity>
            </View>
          ))}
          {hiddenCount > 0 && (
            <View style={[styles.chip, styles.moreChip]}>
              <Text style={styles.moreChipText}>+{hiddenCount} more</Text>
            </View>
          )}
        </View>
      )}

      {/* Phone entry row */}
      <View style={styles.entryRow}>
        <View style={styles.phoneWrapper}>
          <PhoneInputComponent
            ref={phoneRef}
            value={phone}
            onChangeText={setPhone}
            onChange={setPhone}
            placeholder="Enter phone number"
            initialCountry={country}
            style={styles.phoneInput}
          />
        </View>
        <TouchableOpacity
          style={[styles.addBtn, (!phone.trim() || isDisabled) && styles.addBtnDisabled]}
          onPress={handleAdd}
          disabled={!phone.trim() || isDisabled}
          activeOpacity={0.75}
        >
          <Ionicons name="add" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Optional contact name */}
      <Input
        value={name}
        onChangeText={setName}
        placeholder="Contact name (optional)"
        editable={!isDisabled}
      />

      {/* Import action buttons */}
      <View style={styles.actionsRow}>
        {onImportFromDevice && Platform.OS !== 'web' && (
          <TouchableOpacity
            style={[styles.actionBtn, isDisabled && styles.actionBtnDisabled]}
            onPress={onImportFromDevice}
            disabled={isDisabled}
            activeOpacity={0.75}
          >
            <Ionicons
              name="people-outline"
              size={20}
              color={isDisabled ? colors.text.secondary : colors.primary}
            />
          </TouchableOpacity>
        )}

        {onImportFromFile && (
          <TouchableOpacity
            style={[styles.actionBtn, isDisabled && styles.actionBtnDisabled]}
            onPress={onImportFromFile}
            disabled={isDisabled}
            activeOpacity={0.75}
          >
            <Ionicons
              name="cloud-upload-outline"
              size={20}
              color={isDisabled ? colors.text.secondary : colors.primary}
            />
          </TouchableOpacity>
        )}

        {onDownloadTemplate && (
          <TouchableOpacity
            style={[styles.actionBtn, isDisabled && styles.actionBtnDisabled]}
            onPress={onDownloadTemplate}
            disabled={isDisabled}
            activeOpacity={0.75}
          >
            <Ionicons
              name="download-outline"
              size={20}
              color={isDisabled ? colors.text.secondary : colors.primary}
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  chipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingBottom: spacing.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    maxWidth: 220,
  },
  chipExisting: {
    borderColor: colors.success,
    backgroundColor: 'rgba(40, 167, 69, 0.07)',
  },
  chipInvalid: {
    borderColor: colors.error,
    backgroundColor: 'rgba(220, 53, 69, 0.07)',
  },
  moreChip: {
    backgroundColor: colors.background.secondary,
    borderColor: colors.borderLight,
  },
  moreChipText: {
    fontSize: 12,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  chipIcon: {
    marginRight: 5,
  },
  chipText: {
    fontSize: 13,
    color: colors.text.primary,
    flex: 1,
    fontFamily: typography.fontFamily.primary,
  },
  chipClose: {
    marginLeft: 6,
    padding: 2,
  },
  entryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  phoneWrapper: {
    flex: 1,
  },
  phoneInput: {
    flex: 1,
  },
  addBtn: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addBtnDisabled: {
    backgroundColor: colors.background.disabled,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  actionBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.primary,
  },
  actionBtnDisabled: {
    borderColor: colors.borderMedium,
    opacity: 0.5,
  },
  actionBtnText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '500',
    fontFamily: typography.fontFamily.primary,
  },
  actionBtnTextDisabled: {
    color: colors.text.secondary,
  },
});
